// Shared helpers for the Knowledge-Hub video-analysis feature.
//
// Plan-VideoAnalysis.md §B locked-decision constants + helpers used by both
// `pages/KnowledgeHub.tsx` (the upload modal + URL paste flow) and the
// `components/RichTextEditor.tsx` Insert-Video dialog. Originally lived
// inline in KnowledgeHub.tsx (commit 9aaab92); extracted here when the
// Insert Video flow needed the same code.

import type { Plan } from '../types/billing';

// --- §B.2 — file-size caps per plan tier ---------------------------------
// Decimal MB per team-lead (matches Stripe / S3 / GCS billing & marketing
// copy convention). Shadow Fiend's backend uses the same numbers.
export const VIDEO_ANALYSIS_PLAN_CAP_MB: Record<Plan, number> = {
  trial: 50,
  starter: 100,
  pro: 250,
  business: 500,
  enterprise: 1000,
};

/** "Upgrade to next tier" hint for the file-too-large tooltip. The cap shown
 *  is the NEXT tier's number, mirroring how the rest of the billing UI
 *  references upgrade paths. Enterprise has no next tier. */
export const VIDEO_ANALYSIS_NEXT_TIER: Record<Plan, { plan: Plan; cap: number } | null> = {
  trial: { plan: 'starter', cap: 100 },
  starter: { plan: 'pro', cap: 250 },
  pro: { plan: 'business', cap: 500 },
  business: { plan: 'enterprise', cap: 1000 },
  enterprise: null,
};

// --- §B.5 — fallback monthly quota ---------------------------------------
// Used when the billing payload predates Shadow Fiend's seed migration and
// lacks the `max_video_analyses` key — render the canonical LOCKED tier
// numbers instead of "0 of 0 remaining".
export const VIDEO_ANALYSIS_DEFAULT_QUOTA: Record<Plan, number> = {
  trial: 2,
  starter: 4,
  pro: 10,
  business: 30,
  enterprise: -1,
};

/** Pretty plan label for tooltip + toast copy. Could pull `PLAN_DISPLAY[p].name`
 *  but importing it just for this is not worth the bundle weight. */
export const PLAN_LABELS: Record<Plan, string> = {
  trial: 'Trial',
  starter: 'Starter',
  pro: 'Pro',
  business: 'Business',
  enterprise: 'Enterprise',
};

// --- File / URL detection -----------------------------------------------

const VIDEO_MIME_PREFIX = 'video/';
const VIDEO_EXTENSIONS = [
  'mp4', 'mpeg', 'mov', 'avi', 'flv', 'mpg', 'webm', 'wmv', '3gpp', 'm4v', 'mkv',
];

export const isVideoFile = (file: File): boolean => {
  if (file.type && file.type.startsWith(VIDEO_MIME_PREFIX)) return true;
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  return VIDEO_EXTENSIONS.includes(ext);
};

/** Lightweight YouTube detector. Plan §B.1: toggle is gated to YouTube URLs
 *  only on URL inputs (Gemini natively ingests YouTube; everything else is
 *  unsupported in v1). Substring check is fine — backend re-validates. */
export const isYouTubeUrl = (url: string): boolean => {
  const u = (url || '').trim().toLowerCase();
  return u.includes('youtube.com/') || u.includes('youtu.be/');
};

/** iOS Safari paints NOTHING for a `<video preload="metadata">` thumbnail
 *  unless the element has a `poster` OR its source URL carries a `#t=` media
 *  fragment (which forces Safari to seek + decode that frame). Append a
 *  `#t=0.1` fragment as the poster-frame fallback. Media fragments are
 *  client-only (never sent to the server) so this is safe on presigned S3
 *  URLs and URLs with `?query` params. If the URL already has a `#`, leave
 *  it untouched to avoid double-appending. Encode spaces BEFORE calling. */
export const withPosterFrame = (url: string): string => {
  if (!url || url.includes('#')) return url;
  return `${url}#t=0.1`;
};

// --- HTML builders -------------------------------------------------------

/** Tiny escape for use inside an HTML attribute value. Not a general-purpose
 *  sanitizer — DOMPurify handles render-side scrubbing. We only use this for
 *  the analyzed-video block's `src` attribute (S3 URL) since the rest of the
 *  inserted HTML comes from Gemini and is treated as trusted by the editor. */
export const escapeHtmlAttr = (s: string): string =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/** YouTube watch URL → embed iframe HTML. Mirrors the shape produced by
 *  `preprocessVideoTokens()` in KnowledgeHub.tsx so the analyzed-section
 *  video block is byte-identical to anything else that ships through the
 *  existing pipeline. */
export const youtubeEmbedHtml = (url: string): string => {
  let embedUrl: string | null = null;
  try {
    const u = (url || '').trim();
    if (u.includes('youtube.com/watch')) {
      const v = new URLSearchParams(new URL(u).search).get('v');
      embedUrl = v ? `https://www.youtube.com/embed/${v}?rel=0` : null;
    } else if (u.includes('youtu.be/')) {
      const id = u.split('youtu.be/')[1]?.split('?')[0];
      embedUrl = id ? `https://www.youtube.com/embed/${id}?rel=0` : null;
    }
  } catch {
    /* fall through */
  }
  if (!embedUrl) return `<p><a href="${escapeHtmlAttr(url)}" target="_blank" rel="noopener">${escapeHtmlAttr(url)}</a></p>`;
  return `<p><iframe src="${embedUrl}" frameborder="0" allowfullscreen="true" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" class="w-full aspect-video rounded-lg my-2"></iframe></p>`;
};

/** Build the `<video>` block for an uploaded file. `playsinline` + the
 *  `#t=0.1` fragment make the thumbnail paint on iOS Safari. */
export const uploadedVideoEmbedHtml = (s3Url: string): string =>
  `<p><video controls playsinline preload="metadata" src="${escapeHtmlAttr(withPosterFrame(s3Url))}"></video></p>`;

/** Marker used to (a) detect already-analyzed content on re-edit so we skip
 *  re-analysis (Plan §B.8) and (b) compose the insertion block. Three `<hr/>`
 *  tags are intentional — visually distinctive "AI-generated section" rule. */
export const VIDEO_ANALYSIS_SEPARATOR = '<hr/><hr/><hr/>';

/** Build the editor HTML payload per Plan §B.7 LOCKED:
 *
 *   <p><video controls preload="metadata" src="…"></video></p>     OR
 *   <p><iframe …youtube embed…></iframe></p>
 *
 *   <hr/><hr/><hr/>
 *
 *   <Gemini-generated structured HTML article (h2/h3/p/ul/li)>
 *
 *   <hr/><hr/><hr/>
 *
 * Caller passes the video block (already shaped per source — YouTube embed
 * iframe vs. uploaded-file `<video>`) and the analyzed HTML body. */
export const buildAnalyzedArticleHtml = (videoBlock: string, analyzedHtml: string): string =>
  `${videoBlock}\n${VIDEO_ANALYSIS_SEPARATOR}\n${analyzedHtml}\n${VIDEO_ANALYSIS_SEPARATOR}`;

/** Detect a previously-analyzed payload so future ships can skip re-analysis
 *  (Plan §B.8). Tolerant of TipTap re-rendering — match the bare hr-trio with
 *  optional whitespace between, and the plain-text em-dash fallback if we
 *  ever flip to it. Both shapes count as "already analyzed". */
export const hasAnalyzedMarker = (html: string): boolean => {
  if (!html) return false;
  if (/<hr\s*\/?>\s*<hr\s*\/?>\s*<hr\s*\/?>/i.test(html)) return true;
  if (/― ― ― ―/.test(html)) return true;
  return false;
};

// --- Error mapping --------------------------------------------------------

/** Map an axios error from `knowledgeAIAPI.analyzeVideo` to a friendly
 *  user-facing message. Per Plan §C.2 error UX — never blocks the underlying
 *  save; the source was already created during the upload step. The caller
 *  passes the user's current plan so we can render the upgrade hint for 413
 *  with the right "next tier" cap. */
export const formatAnalyzeError = (err: unknown, plan: Plan | null | undefined): string => {
  const ax = err as { response?: { status?: number; data?: { detail?: string } } };
  const status = ax.response?.status;
  if (status === 402) {
    return "You've used your monthly video analysis quota. Upgrade your plan or save without analysis.";
  }
  if (status === 413) {
    const next = plan ? VIDEO_ANALYSIS_NEXT_TIER[plan] : null;
    const upgradeHint = next
      ? `Upgrade to ${PLAN_LABELS[next.plan]} (${next.cap} MB) or save without analysis.`
      : 'Save without analysis.';
    return `Video exceeds your plan's analysis size limit. ${upgradeHint}`;
  }
  if (status === 415) {
    return 'AI analysis supports YouTube URLs and uploaded files only.';
  }
  if (status === 504) {
    return 'Analysis took too long. Try again or save without analysis.';
  }
  const detail = ax.response?.data?.detail;
  return detail || 'Video analysis failed. You can save without analysis.';
};
